<template>
    <main class="account-dashboard-page">
        <v-container class="account-dashboard-page__container">
            <account-settings-header
                :items="links"
                title="Dashboard"
                subtitle="Your organization’s service activity and account insights."
                :icon="mdiViewDashboardOutline"
            />

            <template v-if="!cdfwUser">
                <section v-if="loadingDashboard" class="dashboard-state" role="status" aria-live="polite">
                    <v-progress-circular indeterminate color="#f0784c" size="28" width="2" />
                    <div>
                        <h2>Preparing your dashboard</h2>
                        <p>Loading the latest activity for your organization.</p>
                    </div>
                </section>
                <section v-else-if="pageError" class="dashboard-state dashboard-state--error" role="alert">
                    <div class="dashboard-state__icon" aria-hidden="true">
                        <v-icon dark>{{ mdiAlertCircleOutline }}</v-icon>
                    </div>
                    <div class="dashboard-state__copy">
                        <span class="dashboard-eyebrow">TEMPORARY ISSUE</span>
                        <h2>We couldn’t load your dashboard</h2>
                        <p>{{ pageError }}</p>
                    </div>
                    <v-btn class="dashboard-retry" outlined @click="retryDashboard">Try again</v-btn>
                </section>
                <section v-else-if="orgResolved && !hasOrg" class="dashboard-state dashboard-state--empty">
                    <div class="dashboard-state__icon" aria-hidden="true">
                        <v-icon>{{ mdiOfficeBuildingOutline }}</v-icon>
                    </div>
                    <span class="dashboard-eyebrow">ORGANIZATION ACCESS</span>
                    <h2>No organization is linked to your account</h2>
                    <p>Contact your organization administrator or our support team to get connected.</p>
                    <v-btn class="dashboard-retry" outlined to="/help-support">Get support</v-btn>
                </section>
                <section v-else-if="orgResolved && hasOrg && !gssOrgId" class="dashboard-state dashboard-state--empty">
                    <div class="dashboard-state__icon" aria-hidden="true">
                        <v-icon>{{ mdiViewDashboardOutline }}</v-icon>
                    </div>
                    <span class="dashboard-eyebrow">DASHBOARD NOT AVAILABLE</span>
                    <h2>Your organization dashboard isn’t set up yet</h2>
                    <p>Contact support if you think you should have access to this dashboard.</p>
                    <v-btn class="dashboard-retry" outlined to="/help-support">Contact support</v-btn>
                </section>
                <section v-else-if="dashboardHtml" class="dashboard-embed" aria-label="Organization dashboard">
                    <div class="dashboard-embed__heading">
                        <div>
                            <span class="dashboard-eyebrow">ORGANIZATION INSIGHTS</span>
                            <h2>Service dashboard</h2>
                        </div>
                        <span class="dashboard-live"><span aria-hidden="true"></span> Dashboard ready</span>
                    </div>
                    <iframe title="Organization service dashboard" :srcdoc="dashboardHtml"></iframe>
                </section>
            </template>
            <section v-else class="dashboard-cdfw">
                <cdfw-dashboard />
            </section>
        </v-container>
    </main>
</template>

<script>
import { navLinks } from '../navigation.js'
import { getDashboard } from '@/api'
import gql from 'graphql-tag'
import CdfwDashboard from './cdfw/index.vue'
import AccountSettingsHeader from '../account_settings_header.vue'
import { mdiAlertCircleOutline, mdiOfficeBuildingOutline, mdiViewDashboardOutline } from '@mdi/js'

export default {
    metaInfo: { title: `Your Account`, subTitle: `Dashboard` },
    name: `Dashboard`,
    components: { AccountSettingsHeader, CdfwDashboard },
    apollo: {
        queryOrg: {
            query: gql`
                query QueryOrg {
                    queryOrg {
                        gssOrgId
                        orgId
                        orgName
                    }
                }
            `,
            update(data) {
                const organization = data && data.queryOrg
                this.orgResolved = true
                this.pageError = ''
                this.hasOrg = !!organization
                this.gssOrgId = organization && organization.gssOrgId ? organization.gssOrgId : ''
                this.dashboardHtml = ''
                if (this.gssOrgId) this.getDash(this.gssOrgId)
            }
        },
        error() {
            this.orgResolved = true
            this.pageError = 'Please try again. Your organization dashboard is temporarily unavailable.'
        }
    },
    data() {
        return {
            links: navLinks,
            mdiAlertCircleOutline,
            mdiOfficeBuildingOutline,
            mdiViewDashboardOutline,
            hasOrg: false,
            gssOrgId: '',
            orgResolved: false,
            dashboardLoading: false,
            pageError: '',
            dashboardHtml: ''
        }
    },
    computed: {
        loadingDashboard() {
            return !this.orgResolved || this.dashboardLoading || this.$apollo.loading
        },
        cdfwUser() {
            // const user = this.$auth.user
            // getac id - for testing
            // return user.org && user.org.branchId && user.org.branchId === '5gkaNK2zrXgi5zSMgW8CT'
            // cdfw id
            // return user.org && user.org.branchId && user.org.branchId === 'Lxd7YpGCGwubLftAl8MJr'

            // still back to old dashboard so far
            return false
        }
    },
    methods: {
        async getDash(id) {
            this.dashboardLoading = true
            this.pageError = ''
            try {
                const response = await getDashboard({ id })
                const html = response && response.data
                if (typeof html !== 'string' || !html.trim()) {
                    throw new Error('The dashboard returned no content.')
                }
                if (this.gssOrgId === id) this.dashboardHtml = html
            } catch (error) {
                if (this.gssOrgId === id) {
                    this.pageError = 'Please try again. If the problem continues, contact support.'
                }
            } finally {
                if (this.gssOrgId === id) this.dashboardLoading = false
            }
        },
        retryDashboard() {
            if (this.gssOrgId) return this.getDash(this.gssOrgId)
            this.orgResolved = false
            this.pageError = ''
            return this.$apollo.queries.queryOrg.refetch()
        }
    }
}
</script>

<style scoped>
.account-dashboard-page {
    position: relative;
    isolation: isolate;
    min-height: 100vh;
    padding: 30px 0 54px;
    color: #f2f4fa;
    background: radial-gradient(ellipse at 88% 0, rgba(219, 81, 32, 0.14), transparent 38rem),
        linear-gradient(145deg, #292c35, #22252d 65%, #2a2d35);
    font-family: 'Work Sans', 'Roboto', sans-serif;
}
.account-dashboard-page::before {
    position: fixed;
    z-index: 0;
    inset: 0;
    background-image: url('https://storage.googleapis.com/gusa-assets/army-BG3.png');
    background-position: center;
    background-size: cover;
    content: '';
    filter: grayscale(1) brightness(0.8);
    mix-blend-mode: soft-light;
    opacity: 0.08;
    pointer-events: none;
}
.account-dashboard-page__container {
    position: relative;
    z-index: 1;
    max-width: 1320px;
}
.dashboard-state,
.dashboard-embed {
    border: 1px solid rgba(255, 255, 255, 0.11);
    border-radius: 12px;
    background: rgba(35, 38, 46, 0.94);
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.17);
}
.dashboard-state {
    display: flex;
    min-height: 190px;
    align-items: center;
    gap: 18px;
    margin-top: 22px;
    padding: 26px;
}
.dashboard-state h2,
.dashboard-embed__heading h2 {
    margin: 4px 0 6px;
    color: #f4f5f8;
    font-size: 21px;
    font-weight: 600;
    letter-spacing: -0.35px;
    line-height: 1.3;
}
.dashboard-state p {
    margin: 0;
    color: #b8bfcc;
    font-size: 12px;
    line-height: 1.65;
}
.dashboard-state__icon {
    display: flex;
    width: 46px;
    height: 46px;
    flex: 0 0 46px;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(240, 120, 76, 0.28);
    border-radius: 12px;
    color: #ff9a74;
    background: rgba(219, 81, 32, 0.1);
}
.dashboard-state__copy {
    flex: 1;
    min-width: 0;
}
.dashboard-eyebrow {
    color: #ff9a74;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.3px;
}
.dashboard-retry.v-btn {
    flex: 0 0 auto;
    min-height: 38px;
    padding: 0 14px;
    border-color: rgba(240, 120, 76, 0.55);
    border-radius: 7px;
    color: #ffb396;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0;
    text-transform: none;
}
.dashboard-retry.v-btn:hover {
    background: rgba(219, 81, 32, 0.12);
}
.dashboard-retry.v-btn:focus-visible {
    outline: 2px solid #ff9a74;
    outline-offset: 3px;
}
.dashboard-state--empty {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 30px;
}
.dashboard-state--empty h2 {
    margin: 0;
}
.dashboard-state--empty .dashboard-retry {
    margin-top: 6px;
}
.dashboard-embed {
    overflow: hidden;
    margin-top: 22px;
    border-top: 2px solid #db5120;
}
.dashboard-embed__heading {
    display: flex;
    min-height: 72px;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.09);
    background: linear-gradient(100deg, rgba(219, 81, 32, 0.08), transparent 55%), #282b33;
}
.dashboard-embed__heading h2 {
    margin: 3px 0 0;
    font-size: 17px;
}
.dashboard-live {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 7px;
    padding: 6px 9px;
    border: 1px solid rgba(120, 190, 150, 0.24);
    border-radius: 999px;
    color: #bddac8;
    background: rgba(70, 130, 95, 0.11);
    font-size: 10px;
    font-weight: 600;
}
.dashboard-live span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #79bf91;
    box-shadow: 0 0 8px rgba(121, 191, 145, 0.6);
}
.dashboard-embed iframe {
    display: block;
    width: 100%;
    min-height: 72vh;
    border: 0;
    background: #fff;
}
.dashboard-cdfw {
    margin-top: 22px;
}
@media (max-width: 600px) {
    .account-dashboard-page {
        padding-top: 18px;
    }
    .dashboard-state {
        min-height: 0;
        flex-wrap: wrap;
        padding: 18px;
    }
    .dashboard-state__copy {
        flex-basis: calc(100% - 64px);
    }
    .dashboard-retry.v-btn {
        margin-left: 64px;
    }
    .dashboard-state--empty {
        align-items: center;
        text-align: center;
    }
    .dashboard-state--empty .dashboard-retry {
        margin-left: 0;
    }
    .dashboard-embed__heading {
        align-items: flex-start;
        flex-direction: column;
    }
    .dashboard-embed iframe {
        min-height: 75vh;
    }
}
</style>
